import { useMemo } from 'react';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isAxiosError } from 'axios';
import {
  getLeaderboardDisplayName,
  useFulfillmentModelLeaderboard,
} from 'dashboard409a/services/fulfillmentModelLeaderboard';

interface FulfillmentModelsLeaderboardModalProps {
  open: boolean;
  onClose: () => void;
}

export const FulfillmentModelsLeaderboardModal = ({
  open,
  onClose,
}: FulfillmentModelsLeaderboardModalProps) => {
  const { data, isLoading, isFetching, error, refetch } = useFulfillmentModelLeaderboard(open);

  const results = data?.results ?? [];

  const isForbidden = useMemo(
    () => isAxiosError(error) && error.response?.status === 403,
    [error]
  );

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="fulfillment-models-leaderboard-title"
    >
      <DialogTitle id="fulfillment-models-leaderboard-title" sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <EmojiEventsOutlinedIcon fontSize="small" aria-hidden />
            <Typography variant="body1" fontWeight="medium" component="span">
              Fulfillment models leaderboard
            </Typography>
          </Box>
          <Tooltip title="Refresh leaderboard">
            <span>
              <IconButton
                size="small"
                aria-label="Refresh fulfillment models leaderboard"
                data-testid="fulfillment-models-leaderboard-refresh"
                disabled={isLoading || isFetching}
                onClick={() => refetch()}
              >
                {isFetching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers data-testid="fulfillment-models-leaderboard-content">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Active fulfillment models per analyst (reviewer or preparer). Each model is counted once per
          person even if they hold both roles.
        </Typography>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4} role="status" aria-live="polite">
            <CircularProgress size={32} aria-label="Loading leaderboard" />
          </Box>
        )}

        {!isLoading && isForbidden && (
          <Alert severity="warning" data-testid="fulfillment-models-leaderboard-forbidden">
            You do not have permission to view the fulfillment models leaderboard.
          </Alert>
        )}

        {!isLoading && !isForbidden && error && (
          <Alert severity="error" data-testid="fulfillment-models-leaderboard-error">
            Failed to load the leaderboard. Try refreshing.
          </Alert>
        )}

        {!isLoading && !isForbidden && !error && results.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 3, textAlign: 'center' }}
            data-testid="fulfillment-models-leaderboard-empty"
          >
            No fulfillment model assignments to show yet.
          </Typography>
        )}

        {!isLoading && !isForbidden && !error && results.length > 0 && (
          <TableContainer>
            <Table size="small" aria-label="Fulfillment models leaderboard">
              <TableHead>
                <TableRow>
                  <TableCell width={56}>Rank</TableCell>
                  <TableCell>Analyst</TableCell>
                  <TableCell align="right" width={100}>
                    Models
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((entry, index) => (
                  <TableRow key={entry.user_id} data-testid={`fulfillment-models-leaderboard-row-${entry.user_id}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{getLeaderboardDisplayName(entry)}</TableCell>
                    <TableCell align="right">{entry.model_count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2 }}>
        <Button
          size="small"
          sx={{ fontSize: '0.75rem' }}
          onClick={handleClose}
          data-testid="fulfillment-models-leaderboard-close"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
